import { useEffect } from 'react';
import DOMPurify from 'dompurify';

const SITE_URL = 'https://apexmedicalhub.com';

export const usePageMeta = ({ title, description, canonical, image }) => {
  useEffect(() => {
    if (title) {
      document.title = title.includes('Apex') ? title : `${title} | Apex Medical Hub`;
    }
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    if (description) {
      setMeta('description', description);
      setMeta('og:description', description);
      setMeta('twitter:description', description);
    }
    if (title) {
      setMeta('og:title', title);
      setMeta('twitter:title', title);
    }
    if (image) {
      const abs = image.startsWith('http') ? image : `${SITE_URL}${image}`;
      setMeta('og:image', abs);
      setMeta('twitter:image', abs);
    }
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (link) link.setAttribute('href', canonical);
    }
  }, [title, description, canonical, image]);
};

export const ArticleSchema = ({ post }) => {
  if (!post) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    author: {
      '@type': 'Person',
      name: post.author_name || 'Equipe Apex Medical Hub',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Apex Medical Hub',
    },
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${post.slug}`,
    },
  };
  if (post.featured_image) {
    schema.image = post.featured_image;
  }
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(schema)) }}
    />
  );
};

export const FAQSchema = ({ items }) => {
  if (!items || items.length === 0) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(schema)) }}
    />
  );
};

export const BreadcrumbSchema = ({ items }) => {
  if (!items || items.length === 0) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${SITE_URL}${item.url}` : undefined,
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(schema)) }}
    />
  );
};

export const ServiceSchema = ({ service }) => {
  if (!service) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.full_description || service.short_description,
    provider: {
      '@type': 'Organization',
      name: 'Apex Medical Hub',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Brazil',
    },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(schema)) }}
    />
  );
};
